import {
  ACCOUNT_TYPES,
  DEMO_ACCOUNT_NECCESARY_DATA,
  DEMO_ACCOUNT_OPTIONS,
  STANDARD_ACCOUNT_NECCESARY_DATA,
  STANDARD_ACCOUNT_OPTIONS,
  INVESTOR_ACCOUNT_OPTIONS,
  INVESTOR_ACCOUNT_NECCESARY_DATA,
  MASTER_ACCOUNT_OPTIONS,
  MASTER_ACCOUNT_NECCESARY_DATA,
} from 'common/constants/account.constants';
import { INITIAL_DEMO_DEPOSIT } from 'common/constants/trading.constants';
import { stores } from 'stores';
import { AccountService } from './account.service';
import { captureError } from '../utils/error.util';

class MakeAccountService {
  constructor() {
    this.servers = [];
  }

  init() {
    stores.AccountsStore.getServers()
      .then((servers) => {
        this.servers = servers;
      })
      .catch((error) => captureError(error));
  }

  findProvider(accountType) {
    return this.servers.find(
      (server) => server.account_type_key === accountType
    )?.provider;
  }

  /**
   * @param {string} [name]
   * @param {number} leverage
   */
  async makeDemoAccount({ name, leverage }) {
    let accounts = [];

    try {
      const provider = this.findProvider(ACCOUNT_TYPES.forexDemo);
      const requestBody = {
        ...DEMO_ACCOUNT_NECCESARY_DATA,
        provider,
        name: name || DEMO_ACCOUNT_OPTIONS.name,
        leverage:
          leverage.toString() || DEMO_ACCOUNT_OPTIONS.leverage.toString(),
      };

      await AccountService.createAccount(requestBody);

      accounts = await stores.AccountsStore.fetchAccounts({
        needUpdate: true,
      });

      const item = accounts.find((elem) => {
        return (
          elem._account.account_type === ACCOUNT_TYPES.forexDemo &&
          elem._account.balance === 0
        );
      });

      const [{ guid }] = await AccountService.getPSPList({
        id: item?._account.account,
        type: item?._account.account_type,
      });
      const data = {
        amount: INITIAL_DEMO_DEPOSIT,
        pspguid: guid,
        account: item._account,
        payload: {},
      };

      await AccountService.makeDeposit(data);
      accounts = await stores.AccountsStore.fetchAccounts({
        needUpdate: true,
        needClear: true,
      });

      return;
    } catch (error) {
      captureError(error);
    }
  }

  /**
   * @param {string} [name]
   * @param {number} leverage
   */
  async makeStandardAccount({ name, leverage }) {
    try {
      const provider = this.findProvider(ACCOUNT_TYPES.forexReal);
      const requestBody = {
        ...STANDARD_ACCOUNT_NECCESARY_DATA,
        provider,
        name: name || STANDARD_ACCOUNT_OPTIONS.name,
        leverage:
          leverage.toString() || STANDARD_ACCOUNT_OPTIONS.leverage.toString(),
      };
      return await AccountService.createAccount(JSON.stringify(requestBody));
    } catch (error) {
      captureError(error);
    }
  }

  /**
   * @param {number|string} master_account_id
   * @param {boolean} termAndCondition
   */
  async makeInvestorAccount({ master_account_id, termAndCondition }) {
    const provider = this.findProvider(ACCOUNT_TYPES.investorReal);
    const requestBody = {
      ...INVESTOR_ACCOUNT_NECCESARY_DATA,
      server_code: provider,
      master_account_id:
        Number(master_account_id) ||
        Number(INVESTOR_ACCOUNT_OPTIONS.master_account_id),
      termAndCondition:
        termAndCondition || INVESTOR_ACCOUNT_OPTIONS.termAndCondition,
    };

    return await AccountService.createInvestorAccount(requestBody);
  }

  /**
   * @param {number} min_copy_coefficient
   * @param {number} max_copy_coefficient
   * @param {number} min_deposit
   * @param {number} commission
   * @param {boolean} termAndCondition
   */
  async makeMasterAccount({
    min_copy_coefficient,
    max_copy_coefficient,
    min_deposit,
    commission,
    termAndCondition,
  }) {
    const provider = this.findProvider(ACCOUNT_TYPES.masterReal);
    const requestBody = {
      ...MASTER_ACCOUNT_NECCESARY_DATA,
      server_code: provider,
      min_copy_coefficient:
        min_copy_coefficient || MASTER_ACCOUNT_OPTIONS.min_copy_coefficient,
      max_copy_coefficient:
        max_copy_coefficient || MASTER_ACCOUNT_OPTIONS.max_copy_coefficient,
      min_deposit: min_deposit || MASTER_ACCOUNT_OPTIONS.min_deposit,
      commission: commission || MASTER_ACCOUNT_OPTIONS.commission,
      termAndCondition:
        termAndCondition || MASTER_ACCOUNT_OPTIONS.termAndCondition,
    };
    return await AccountService.createMasterAccount(requestBody);
  }
}

export const makeAccountService = new MakeAccountService();
